import React, { useEffect, useRef, useState } from 'react';
import { toast } from '../../../ui/toast';
import { SearchIconNewUI } from '../../../ui/icons/icons';
import { getUserAssessments, getDashboardWellness } from '../../../utils/api_service/dashboard';
import { getApplicationsDetails, getDashboardDetails, getInterviewsDetails } from '../../../utils/api_service/user_api';
import ParticipantCard from './ParticipantCard';
import WellnessDashboard, { WellnessInviteData } from './WellnessDashboard';

export type ParticipantCardData = {
  id: string;
  assessmentTitle: string;
  isActive: boolean;
  startDate: number;
  endDate: number;
  status: string;
};

// GET /user/profile/applications. The opening and its job description can be
// deleted while the application row survives, so the title comes back null;
// the status arrives already collapsed into a statusConfig group, under `group`.
type ApplicationData = {
  jobApplicantId: string;
  jobOpeningId: string;
  jobTitle: string | null;
  group: string;
  jobReference?: string | null;
  createdAt: string | null;
};

// GET /user/profile/dashboard returns the same rows, but names the group
// `applicantStatus`.
type LatestApplication = Omit<ApplicationData, 'group'> & { applicantStatus: string };

type DashboardData = {
  counts: {
    activeApplications: number;
    pendingAssessments: number;
    upcomingInterviews: number;
  };
  latestApplications: LatestApplication[];
  upcomingAssessments: any[];
  upcomingInterviews: any[];
};

type Tab = 'applications' | 'assessments' | 'interviews' | 'resumes'| 'wellness';

const PAGE_SIZE = 9;
const WELLNESS_PAGE_SIZE = 5;

const tabs: { id: Tab; label: string }[] = [
  { id: 'applications', label: 'Applications' },
  { id: 'assessments', label: 'Assessments' },
  { id: 'interviews', label: 'Interviews' },
  { id: 'resumes', label: 'Resume' },
  { id: 'wellness', label: 'Wellness' },
];

// Keyed on the groups returned by GET /user/profile/dashboard — the backend's
// _status_to_group() collapses every applicant status into exactly one of these.
const statusConfig: Record<string, { label: string; className: string }> = {
  'Applied': {
    label: 'Applied',
    className: 'bg-blue-50 text-blue-600 border border-blue-200',
  },
  'Not Selected': {
    label: 'Not Selected',
    className: 'bg-red-50 text-red-500 border border-red-200',
  },
  'Offered': {
    label: 'Offered',
    className: 'bg-purple-50 text-purple-600 border border-purple-200',
  },
};

const getStatusConfig = (status?: string | null) =>
    (status ? statusConfig[status] : undefined) ?? {
      // The backend collapses an unknown status into "Applied"; do the same
      // rather than render a badge with no label.
      label: status || 'Applied',
      className: 'bg-slate-100 text-slate-500 border border-slate-200',
    };

const ApplicationCard = ({ application }: { application: ApplicationData }) => {
  const { label, className } = getStatusConfig(application.group);

  const dateOptions = { day: 'numeric', month: 'short', year: 'numeric' } as const;
  const formattedDate = application.createdAt
      ? new Date(application.createdAt).toLocaleDateString('en-GB', dateOptions)
      : '\u2014';

  const jobTitle = (application.jobTitle || 'Untitled role').replace(/^\*/, '');

  return (
      <div className='bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-md transition-shadow duration-200 p-6 flex flex-col gap-4'>
        <div className='flex items-start justify-between'>
          <div className='w-10 h-10 bg-emerald-50 rounded-xl flex items-center justify-center flex-shrink-0'>
            <svg className='w-5 h-5 text-emerald-500' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.5}
                    d='M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' />
            </svg>
          </div>
          <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${className}`}>
          { label }
        </span>
        </div>
        <div>
          <p className='text-slate-800 font-semibold text-[15px] leading-snug'>{ jobTitle }</p>
          <p className='text-slate-400 text-xs mt-1'>Applied { formattedDate }</p>
        </div>
        <div className='pt-3 border-t border-slate-100 flex items-center justify-between'>
          <span className='text-[11px] text-slate-400 font-medium'>Application ID</span>
          <span className='text-[11px] font-mono text-slate-500 truncate max-w-[140px]'>
          { application.jobApplicantId }
        </span>
        </div>
      </div>
  );
};

// ── Pagination Control ───────────────────────────────────────────────────────
const Pagination = ({
                      page,
                      totalCount,
                      pageSize,
                      onPageChange,
                    }: {
  page: number;
  totalCount: number;
  pageSize: number;
  onPageChange: (p: number) => void;
}) => {
  const totalPages = Math.ceil(totalCount / pageSize);
  if (totalPages <= 1) return null;

  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, totalCount);

  return (
      <div className='relative z-10 flex items-center justify-between py-4'>
        <p className='text-xs text-slate-400'>
          Showing <span className='font-semibold text-slate-600'>{ from }–{ to }</span> of{ ' ' }
          <span className='font-semibold text-slate-600'>{ totalCount }</span>
        </p>
        <div className='flex items-center gap-1'>
          <button
              onClick={() => onPageChange(page - 1)}
              disabled={page === 1}
              className='w-8 h-8 flex items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors'
          >
            <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M15 19l-7-7 7-7' />
            </svg>
          </button>

          { Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
              .reduce<(number | '...')[]>((acc, p, idx, arr) => {
                if (idx > 0 && typeof arr[idx - 1] === 'number' && (p as number) - (arr[idx - 1] as number) > 1) {
                  acc.push('...');
                }
                acc.push(p);
                return acc;
              }, [])
              .map((p, idx) =>
                      p === '...' ? (
                          <span key={`ellipsis-${idx}`} className='w-8 h-8 flex items-center justify-center text-slate-400 text-sm'>
                …
              </span>
                      ) : (
                          <button
                              key={p}
                              onClick={() => onPageChange(p as number)}
                              className={`w-8 h-8 flex items-center justify-center rounded-lg text-sm font-medium transition-colors ${
                                  page === p
                                      ? 'bg-emerald-500 text-white shadow-sm'
                                      : 'border border-slate-200 text-slate-600 hover:bg-slate-50'
                              }`}
                          >
                            { p }
                          </button>
                      ),
              ) }

          <button
              onClick={() => onPageChange(page + 1)}
              disabled={page === totalPages}
              className='w-8 h-8 flex items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors'
          >
            <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 5l7 7-7 7' />
            </svg>
          </button>
        </div>
      </div>
  );
};

// ── Main Component ───────────────────────────────────────────────────────────
const ParticipantDashboard = () => {
  const [activeTab, setActiveTab] = useState<Tab>('applications');
  const [userAssessments, setUserAssessments] = useState<ParticipantCardData[]>([]);
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [applicationData, setApplicationData] = useState<ApplicationData[] | null>(null);
  const [interviewData, setInterviewData] = useState<any[] | null>(null);
  const [wellnessData, setWellnessData] = useState<WellnessInviteData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [dashLoading, setDashLoading] = useState<boolean>(false);
  const [wellnessLoading, setWellnessLoading] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [page, setPage] = useState(1);

  // Separate total counts per paginated tab
  const [appTotalCount, setAppTotalCount] = useState(0);
  const [interviewTotalCount, setInterviewTotalCount] = useState(0);
  const [wellnessTotalCount, setWellnessTotalCount] = useState(0);

  const firstName = localStorage.getItem('firstname') ?? 'there';

  // Track mounted state to avoid setState on unmounted component (packaged build)
  const isMountedRef = useRef(true);
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Reset to page 1 when tab or search changes
  useEffect(() => {
    setPage(1);
  }, [activeTab, searchTerm]);

  useEffect(() => {
    let cancelled = false;
    if (activeTab === 'assessments') {
      setLoading(true);
      const debounceFetch = setTimeout(() => {
        getUserAssessments(searchTerm)
            .then((response) => {
              if (cancelled || !isMountedRef.current) return;
              if (!response.ok) throw new Error();
              return response.json();
            })
            .then((data) => {
              if (cancelled || !isMountedRef.current) return;
              if (data) setUserAssessments(data);
            })
            .catch(() => {
              if (cancelled || !isMountedRef.current) return;
              toast.error('Error Fetching User Assessment Data', { position: 'bottom-center' });
            })
            .finally(() => {
              if (cancelled || !isMountedRef.current) return;
              setLoading(false);
            });
      }, 1000);
      return () => {
        cancelled = true;
        clearTimeout(debounceFetch);
      };
    } else if (activeTab === 'applications') {
      getApplicationItems();
    } else if (activeTab === 'interviews') {
      getInterviewsItems();
    } else if (activeTab === 'wellness') {
      getWellnessItems();
    }
    return () => {
      cancelled = true;
    };
  }, [searchTerm, activeTab, page]);  // page included so re-fetch fires on navigation

  useEffect(() => {
    getDashItems();
  }, []);

  const getInterviewsItems = async () => {
    if (!isMountedRef.current) return;
    setDashLoading(true);
    try {
      const res = await getInterviewsDetails(searchTerm, page, PAGE_SIZE);
      if (!isMountedRef.current) return;
      if (res.ok) {
        const result = await res.json();
        if (!isMountedRef.current) return;
        setInterviewData(result.interviews ?? []);
        setInterviewTotalCount(result.totalCount ?? 0);
      }
    } catch (e) {
      console.error(e);
    } finally {
      if (isMountedRef.current) setDashLoading(false);
    }
  };

  const getApplicationItems = async () => {
    if (!isMountedRef.current) return;
    setDashLoading(true);
    try {
      const res = await getApplicationsDetails(searchTerm, page, PAGE_SIZE);
      if (!isMountedRef.current) return;
      if (res.ok) {
        const result = await res.json();
        if (!isMountedRef.current) return;
        setApplicationData(result.applications);
        setAppTotalCount(result.total ?? 0);
      }
    } catch (e) {
      console.error(e);
    } finally {
      if (isMountedRef.current) setDashLoading(false);
    }
  };

  const getWellnessItems = async () => {
    if (!isMountedRef.current) return;
    setWellnessLoading(true);
    try {
      const res = await getDashboardWellness(searchTerm, page, WELLNESS_PAGE_SIZE);
      if (!isMountedRef.current) return;
      if (res.ok) {
        const result = await res.json();
        if (!isMountedRef.current) return;
        setWellnessData(result.assessments ?? []);
        setWellnessTotalCount(result.total ?? 0);
      }
    } catch (e) {
      console.error(e);
    } finally {
      if (isMountedRef.current) setWellnessLoading(false);
    }
  };

  const getDashItems = async () => {
    if (!isMountedRef.current) return;
    setDashLoading(true);
    try {
      const res = await getDashboardDetails();
      if (!isMountedRef.current) return;
      if (res.ok) {
        const result: DashboardData = await res.json();
        if (!isMountedRef.current) return;
        setDashboardData(result);
      }
    } catch (e) {
      console.error(e);
    } finally {
      if (isMountedRef.current) setDashLoading(false);
    }
  };

  const activeApplications = dashboardData?.counts.activeApplications ?? 0;
  const pendingAssessments = dashboardData?.counts.pendingAssessments ?? 0;
  const upcomingInterviews = dashboardData?.counts.upcomingInterviews ?? 0;

  return (
      <div className='relative px-5 flex flex-col gap-8 overflow-hidden'>

        { /* Welcome Banner */ }
        <div className='bg-gradient-to-r from-emerald-600 to-emerald-500 rounded-2xl shadow-xl p-8'>
          <div className='flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6'>
            <div>
              <h1 className='text-3xl font-bold text-white mb-2'>
                Welcome back, { firstName }!
              </h1>
              <p className='text-emerald-100 text-sm'>
                You have { activeApplications } active application{ activeApplications !== 1 ? 's' : '' } in progress.
              </p>
            </div>
          </div>
        </div>

        { /* Stats Cards */ }
        <div className='grid grid-cols-2 md:grid-cols-3 gap-6'>
          <div className='group relative text-center p-6 bg-gradient-to-br from-emerald-50 to-emerald-100/50 rounded-2xl hover:from-emerald-100 hover:to-emerald-200/50 transition-all duration-300 cursor-pointer'>
            <div className='w-14 h-14 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl mx-auto mb-4 flex items-center justify-center shadow-lg group-hover:shadow-xl group-hover:scale-110 transition-all duration-300'>
              <svg className='w-7 h-7 text-white' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'/>
              </svg>
            </div>
            <div className='text-3xl font-bold bg-gradient-to-br from-emerald-600 to-emerald-700 bg-clip-text text-transparent mb-1'>
              { dashLoading ? '—' : activeApplications }
            </div>
            <div className='text-sm font-medium text-gray-600'>Active Applications</div>
          </div>

          <div className='group relative text-center p-6 bg-gradient-to-br from-orange-50 to-orange-100/50 rounded-2xl hover:from-orange-100 hover:to-orange-200/50 transition-all duration-300 cursor-pointer'>
            <div className='w-14 h-14 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl mx-auto mb-4 flex items-center justify-center shadow-lg group-hover:shadow-xl group-hover:scale-110 transition-all duration-300'>
              <svg className='w-7 h-7 text-white' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01'/>
              </svg>
            </div>
            <div className='text-3xl font-bold bg-gradient-to-br from-orange-600 to-orange-700 bg-clip-text text-transparent mb-1'>
              { dashLoading ? '—' : pendingAssessments }
            </div>
            <div className='text-sm font-medium text-gray-600'>Pending Assessments</div>
          </div>

          <div className='group relative text-center p-6 bg-gradient-to-br from-purple-50 to-purple-100/50 rounded-2xl hover:from-purple-100 hover:to-purple-200/50 transition-all duration-300 cursor-pointer'>
            <div className='w-14 h-14 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl mx-auto mb-4 flex items-center justify-center shadow-lg group-hover:shadow-xl group-hover:scale-110 transition-all duration-300'>
              <svg className='w-7 h-7 text-white' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z'/>
              </svg>
            </div>
            <div className='text-3xl font-bold bg-gradient-to-br from-purple-600 to-purple-700 bg-clip-text text-transparent mb-1'>
              { dashLoading ? '—' : upcomingInterviews }
            </div>
            <div className='text-sm font-medium text-gray-600'>Upcoming Interviews</div>
          </div>
        </div>

        { /* Tabs */ }
        <div className='relative z-10 flex gap-1 bg-slate-100 p-1 rounded-xl w-fit'>
          { tabs.map((tab) => (
              <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-5 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                      activeTab === tab.id
                          ? 'bg-white text-emerald-600 shadow-sm'
                          : 'text-slate-500 hover:text-slate-700'
                  }`}
              >
                { tab.label }
              </button>
          )) }
        </div>

        <div className='relative z-10 flex'>
          <div className='relative flex items-center pr-2 shadow-sm'>
            <SearchIconNewUI className='absolute left-3 w-4 h-4 text-slate-700'/>
            <input
                type='text'
                placeholder='Search...'
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className='w-72 bg-white-100/80 border border-slate-200/50 rounded-xl py-2 pl-10 pr-4 text-[13px] placeholder:text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500/50 transition-all font-medium text-slate-700'
            />
          </div>
        </div>

        { /* Applications */ }
        { activeTab === 'applications' && (
            dashLoading ? (
                <div className='relative z-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8'>
                  { [...Array(4)].map((_, index) => (
                      <div key={index} className='h-[180px] animate-pulse bg-gray-200 rounded-2xl'/>
                  )) }
                </div>
            ) : applicationData?.length === 0 ? (
                <div className='relative z-10 flex flex-col items-center justify-center py-24 text-center'>
                  <div className='w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center mb-4'>
                    <svg className='w-8 h-8 text-emerald-500' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.5} d='M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'/>
                    </svg>
                  </div>
                  <p className='text-slate-700 font-semibold text-lg'>No Applications Yet</p>
                  <p className='text-slate-400 text-sm mt-1'>Jobs you apply to will be tracked here.</p>
                </div>
            ) : (
                <>
                  <div className='relative z-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8'>
                    { applicationData?.map((application) => (
                        <ApplicationCard key={application.jobApplicantId} application={application}/>
                    )) }
                  </div>
                  <Pagination
                      page={page}
                      totalCount={appTotalCount}
                      pageSize={PAGE_SIZE}
                      onPageChange={setPage}
                  />
                </>
            )
        ) }

        { /* Assessments */ }
        { activeTab === 'assessments' && (
            loading ? (
                <div className='relative z-10 flex flex-wrap gap-4'>
                  { [...Array(6)].map((_, index) => (
                      <div key={index} className='h-[400px] w-full md:basis-1/2-gap-4 lg:basis-1/3-gap-4 xl:basis-1/4-gap-4 gap-4 animate-pulse bg-gray-200 rounded-[40px]'/>
                  )) }
                </div>
            ) : (
                <div className='relative z-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8'>
                  { userAssessments.map((assessment, index) => (
                      <ParticipantCard userAssessment={assessment} key={index}/>
                  )) }
                </div>
            )
        ) }

        { /* Interviews */ }
        { activeTab === 'interviews' && (
            dashLoading ? (
                <div className='relative z-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8'>
                  { [...Array(4)].map((_, index) => (
                      <div key={index} className='h-[180px] animate-pulse bg-gray-200 rounded-2xl'/>
                  )) }
                </div>
            ) : interviewData?.length === 0 || interviewData === null ? (
                <div className='relative z-10 flex flex-col items-center justify-center py-24 text-center'>
                  <div className='w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center mb-4'>
                    <svg className='w-8 h-8 text-emerald-500' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.5} d='M15 10l4.553-2.069A1 1 0 0121 8.868V15.13a1 1 0 01-1.447.9L15 14M3 8a2 2 0 012-2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8z'/>
                    </svg>
                  </div>
                  <p className='text-slate-700 font-semibold text-lg'>No Interviews Yet</p>
                  <p className='text-slate-400 text-sm mt-1'>Your scheduled interviews will appear here.</p>
                </div>
            ) : (
                <>
                  { /* Render your interview cards here once the data shape is known */ }
                  <div className='relative z-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8'>
                    { /* {interviewData.map(...)} */ }
                  </div>
                  <Pagination
                      page={page}
                      totalCount={interviewTotalCount}
                      pageSize={PAGE_SIZE}
                      onPageChange={setPage}
                  />
                </>
            )
        ) }

        { /* Resume */ }
        { activeTab === 'resumes' && (
            <div className='relative z-10 flex flex-col items-center justify-center py-24 text-center'>
              <div className='w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center mb-4'>
                <svg className='w-8 h-8 text-emerald-500' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.5} d='M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z'/>
                </svg>
              </div>
              <p className='text-slate-700 font-semibold text-lg'>No Resume Uploaded</p>
              <p className='text-slate-400 text-sm mt-1'>Upload your resume to get started with applications.</p>
            </div>
        ) }

        { activeTab === 'wellness' && (
            <WellnessDashboard
                invites={wellnessData}
                totalCount={wellnessTotalCount}
                loading={wellnessLoading}
                page={page}
                pageSize={WELLNESS_PAGE_SIZE}
                onPageChange={setPage}
                onRefresh={getWellnessItems}
            />
        ) }
      </div>
  );
};

export default ParticipantDashboard;